import { useMemo } from 'react'
import { Lock } from 'lucide-react'
import {
  type StructureData, type Group, type GroupType,
  GROUP_TYPE_LABELS, fullGroupName,
} from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { useGlobalStandardGroups, isGlobalGroupId } from '../useGlobalStandardGroups.ts'
import {
  FilterBar,
  FilterChip,
  MatrixTable,
  type MatrixColumn,
  type MatrixColumnGroup,
  type MatrixRow,
} from '@/components/ui'
import { GROUP_TYPE_HEADER_CLASS } from './matrix/typeColors.ts'
import { cn } from '@/lib/utils.ts'

/**
 * Abteilungs-Matrix: Zeilen = AB-Gruppen, Spalten = LI/FU/SW-Gruppen.
 * Zelle zeigt AD-Nesting („Ist die AB-Gruppe Mitglied der LI/FU/SW-Gruppe?").
 * Nutzt die zentrale `MatrixTable`-Komponente.
 */

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
  groupTypes: GroupType[]
  activeTypes: GroupType[]
  onActiveTypesChange: (next: GroupType[]) => void
}

interface AbRow extends MatrixRow {
  ab: Group
  abIsGlobal: boolean
}

interface GroupCol extends MatrixColumn {
  group: Group
  isGlobal: boolean
}

export function DepartmentGroupMatrix({ data, update, groupTypes, activeTypes, onActiveTypesChange }: Props) {
  const a = useAnonymize()
  const globalGroups = useGlobalStandardGroups()

  const allGroups = useMemo<Group[]>(
    () => [...globalGroups, ...data.groups],
    [globalGroups, data.groups],
  )

  const abGroups = useMemo(() => allGroups.filter((g) => g.type === 'AB'), [allGroups])
  const groupsByType = useMemo(() => {
    const map = new Map<GroupType, Group[]>()
    for (const t of groupTypes) map.set(t, allGroups.filter((g) => g.type === t))
    return map
  }, [allGroups, groupTypes])

  const visibleTypes = useMemo(
    () => groupTypes.filter((t) => activeTypes.includes(t)),
    [groupTypes, activeTypes],
  )

  function toggleType(t: GroupType) {
    if (activeTypes.includes(t)) {
      onActiveTypesChange(groupTypes.filter((g) => activeTypes.includes(g) && g !== t))
    } else {
      onActiveTypesChange(groupTypes.filter((g) => activeTypes.includes(g) || g === t))
    }
  }

  function toggleNesting(abId: string, targetId: string) {
    if (isGlobalGroupId(abId)) return
    const nextGroups = data.groups.map((g) => {
      if (g.id !== abId) return g
      const current = new Set(g.memberOfGroupIds ?? [])
      if (current.has(targetId)) current.delete(targetId)
      else current.add(targetId)
      const arr = [...current]
      return { ...g, memberOfGroupIds: arr.length > 0 ? arr : undefined }
    })
    update({ groups: nextGroups })
  }

  function isNested(abId: string, targetId: string): boolean {
    const ab = data.groups.find((g) => g.id === abId)
    return ab?.memberOfGroupIds?.includes(targetId) ?? false
  }

  const columnGroups = useMemo<MatrixColumnGroup<GroupCol>[]>(() => {
    return visibleTypes
      .filter((t) => (groupsByType.get(t)?.length ?? 0) > 0)
      .map((t) => {
        const grps = groupsByType.get(t) ?? []
        return {
          id: t,
          label: (
            <span>
              <span className="font-mono mr-1">{t}</span>
              {GROUP_TYPE_LABELS[t]}
              <span className="ml-1.5 text-[10px] opacity-70">({grps.length})</span>
            </span>
          ),
          headerClass: GROUP_TYPE_HEADER_CLASS[t],
          columns: grps.map<GroupCol>((g) => {
            const isGlobal = isGlobalGroupId(g.id)
            return {
              id: g.id,
              group: g,
              isGlobal,
              label: (
                <span className={cn('flex items-center gap-0.5', isGlobal && 'text-gray-500 italic')}>
                  {isGlobal && <Lock className="w-2.5 h-2.5 flex-shrink-0" />}
                  {a.token(g.teamName || g.name)}
                </span>
              ),
              title: isGlobal
                ? `Globale Standard-Gruppe — ${g.name}${g.description ? ` · ${g.description}` : ''}`
                : fullGroupName(data.domainPrefix, g.type, g.name),
              widthPx: 32,
            }
          }),
        }
      })
  }, [visibleTypes, groupsByType, a, data.domainPrefix])  

  const rows = useMemo<AbRow[]>(
    () =>
      abGroups.map((ab) => {
        const abIsGlobal = isGlobalGroupId(ab.id)
        return {
          id: ab.id,
          ab,
          abIsGlobal,
          label: (
            <span className="font-medium text-caption">
              {abIsGlobal && <Lock className="w-2.5 h-2.5 inline mr-1 text-gray-400" />}
              <span className={cn(abIsGlobal && 'text-gray-500 italic')}>
                {a.token(ab.teamName || ab.name)}
              </span>
            </span>
          ),
        }
      }),
    [abGroups, a],  
  )

  const renderCell = (r: AbRow, c: GroupCol) => {
    const checked = isNested(r.ab.id, c.group.id)
    const disabled = r.abIsGlobal
    const title = disabled
      ? `${r.ab.name}: globale Standard-Gruppe — nicht editierbar`
      : checked
        ? `${r.ab.name} ist Mitglied von ${c.group.name} — Klick zum Entfernen`
        : `${r.ab.name} ist NICHT Mitglied von ${c.group.name} — Klick zum Hinzufuegen`
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => toggleNesting(r.ab.id, c.group.id)}
        title={title}
        className={cn(
          'w-full h-full text-[11px] font-mono px-1 py-1 border-0 select-none focus:outline-none',
          checked ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'bg-white text-gray-300',
          disabled
            ? 'cursor-not-allowed'
            : 'cursor-pointer hover:ring-1 hover:ring-inset hover:ring-brand-500 focus:ring-1 focus:ring-inset focus:ring-brand-500',
        )}
      >
        {checked ? '✓' : '·'}
      </button>
    )
  }

  return (
    <div className="space-y-3">
      <FilterBar>
        <span className="text-caption font-medium text-gray-600 mr-1">Anzeigen:</span>
        {groupTypes.map((t) => {
          const active = activeTypes.includes(t)
          const count = groupsByType.get(t)?.length ?? 0
          return (
            <FilterChip
              key={t}
              active={active}
              count={count}
              onClick={() => toggleType(t)}
              onRemove={active ? () => toggleType(t) : undefined}
              title={active ? `${GROUP_TYPE_LABELS[t]} ausblenden` : `${GROUP_TYPE_LABELS[t]} einblenden`}
            >
              <span className="font-mono mr-1">{t}</span>
              <span>{GROUP_TYPE_LABELS[t]}</span>
            </FilterChip>
          )
        })}
      </FilterBar>

      <MatrixTable<AbRow, GroupCol>
        rows={rows}
        labelColumns={[
          {
            id: 'abteilung',
            header: 'Abteilung',
            widthPx: 220,
            render: (r) => r.label,
          },
        ]}
        columnGroups={columnGroups}
        renderCell={renderCell}
        verticalHeaderHeight={80}
      />
    </div>
  )
}
